import {Button, Dropdown, Menu, Modal, Space, Tooltip} from "antd";
import ApiService from "../../../core/service/api-service";
import {DownOutlined} from "@ant-design/icons";
import Flex from "../../../core/components/Flex";
import {useDataGrid} from "../../../core/components/data-grid/DataGridContext";
import "../../../core/components/data-grid/grid-dropdown.css";
import {useTranslation} from "react-i18next";
import {PERMISSIONS, useUser} from "../../../core/context/UserContext";
import {useModal} from "../../../core/context/ModalContext";
import StudyCreateModal from "../StudyCreateModal";
import {useEffect, useState} from "react";
import useKeypress from "react-use-keypress";
import {HubConnection, HubConnectionBuilder} from "@microsoft/signalr";
import StudyUserAssignModal from "../StudyUserAssignModal";
import Colors from "../../../core/style/Colors";
import useFocusListener from "../../../core/components/FocusListener";
import StudyReadUsersModal from "../StudyReadUsersModal";
import moment from "moment";
import { FaCompactDisc } from "react-icons/fa";
import StudyExportModal from "../StudyExportModal";
import StudyShareModal from "../StudyShareModal";
import MergeStudiesModal from "../MergeStudiesModal";
import DicomZipDownloadModal from "../DicomZipDownloadModal";
import { confirmPermanentDelete } from "../StudyPermanentDeleteConfirm";
import { copyToClipboard } from "../../../core/utils/clipboard-utils";

const colors = [
    {color: Colors.deletedRow, text: 'DELETED-ROW'},
    {color: Colors.emergencyRow, text: 'EMERGENCY-ROW'},
    {color: Colors.noReportRow, text: 'NO-REPORT-ROW'},
    {color: Colors.confirmedRow, text: 'HAS-REPORT-ROW'},
    {color: Colors.notConfirmedRow, text: 'NOT-CONFIRMED-ROW'},
    {color: Colors.selectedRow, text: 'SELECTED-ROW'},
]

export default function StudyGridTools()
{
    const {t} = useTranslation();
    const {selectedRows, refreshDataGrid, tableId} = useDataGrid();
    const {hasPermission, user} = useUser();
    const {openModal, closeModal, modal} = useModal();
    const isFocus = useFocusListener({elementId: tableId})

    useKeypress(['t', 'd', 'v', 'w'], async (e) => {
        if(!isFocus)
            return

        if(e)
        {
            switch (e.key)
            {
                case 't': {
                    await setRead()
                    break;
                }
                case 'd': {
                    await openViewer();
                    break;
                }
                case 'v': {
                    await downloadVoiceRecords();
                    break;
                }
                case 'w': {
                    if (!user?.config?.enableWebViewer) {
                        return;
                    }
                    
                    openWebViewer()
                    break;
                }
                default: {
                    return;
                }
            }
        }
    })

    // GÖRÜNTÜ AÇMA KİLİDİ: çekimler açılmadan önce sunucuya sorulur (Weasis ve web viewer aynı yol).
    // Başka doktorun süren kilidindeki çekimler listeden düşer ve kullanıcıya gösterilir; kalanlar bu
    // kullanıcıya atanır. Kilide tabi olmayan kullanıcıda sunucu listeyi olduğu gibi döner.
    // Atama grid'i kendiliğinden yenilemez: kullanıcı bu uyarıdaki "Yenile" ile yeniler.
    const claimStudiesForViewing = async (idList) => {
        const response = await ApiService.StudyOpenLock.Claim(idList)
        if (!response.success)
            return []

        const skipped = response.data?.skipped || []
        if (skipped.length > 0) {
            Modal.warning({
                title: t('Some studies were not opened'),
                width: 640,
                okCancel: true,
                okText: t('Refresh'),
                cancelText: t('Close'),
                onOk: () => refreshDataGrid(),
                content: (
                    <>
                        <p>{t('STUDY_OPEN_SKIPPED_DESC')}</p>
                        <ul style={{paddingLeft: 18, marginBottom: 0}}>
                            {skipped.map(s => (
                                <li key={s.studyId}>
                                    {t('STUDY_OPEN_SKIPPED_ITEM', {
                                        patient: s.patientName || '-',
                                        accession: s.accessionNumber || '-',
                                        doctor: s.doctorName || '-',
                                        until: s.lockedUntil ? moment(s.lockedUntil).format('HH:mm') : '-',
                                        interpolation: {escapeValue: false},
                                    })}
                                </li>
                            ))}
                        </ul>
                    </>
                ),
            })
        }

        return response.data?.allowedStudyIds || []
    }

    const openWebViewer = async () => {
        if(selectedRows.length === 0)
            return

        const allowedStudyIds = await claimStudiesForViewing(selectedRows.map(x => x.id))
        if(allowedStudyIds.length === 0)
            return

        const apiBase = window?.AppSettings?.server?.api || ''
        // Viewer artık API ile aynı sunucuda, sabit yolda: ayrı bir adres ayarı (server.viewer) kalmadı.
        const viewerBase = `${apiBase}/api/viewer/`
        const sep = viewerBase.includes('?') ? '&' : '?'
        const studies = allowedStudyIds.map(id => encodeURIComponent(id)).join(',')
        // Çerezli oturumda adres token TAŞIMAZ: sekme aynı origin'e açıldığı için oturum çerezi
        // hem sayfayı hem metadata/DICOM isteklerini yetkilendirir. Token yalnız elde eski bir
        // oturum kaldıysa eklenir (ApiService.tokenQueryParam).
        const url = `${viewerBase}${sep}studies=${studies}${ApiService.tokenQueryParam()}`
        window.open(url, '_blank')
    }

    const makeKos = async () => {
        if(selectedRows.length > 0) {
            const res = await ApiService.Teletip.MakeKos(selectedRows.map(x => ({studyId: x.id})))
        }
    }

    const downloadStudies = async () => {
        if (selectedRows.length === 0) {
            return;
        }

        openModal(t('Download Dicom Zip'), (
            <DicomZipDownloadModal
                studies={selectedRows.map(x => ({
                    studyId: x.id,
                    patientName: x.patientName,
                }))}
                onClose={() => closeModal()}
            />
        ))
    }

    const shareStudies = () => {
        if (selectedRows.length === 0) {
            return;
        }

        // openModal(title, component, onCloseFn, width, top) — KONUMSAL argümanlar.
        // Buraya {width, onClose} diye bir seçenek NESNESİ geçiliyordu: nesne onCloseFn olarak
        // saklanıp kapanışta çağrılıyor ve "onCloseFn is not a function" ile patlıyordu; width de
        // yanlış konumda olduğu için hiç uygulanmıyordu.
        openModal(
            t('Share Studies'),
            <StudyShareModal
                studyIds={selectedRows.map(x => x.id)}
            />,
            null,       // kapanışta ek iş yok (modal zaten kendi kapanıyor)
            1300        // width
        );
    }

    const deleteReadUser = async () => {
        openModal(t('Study Read User'), (
            <StudyReadUsersModal
                studyIdList={selectedRows.map(x => x.id)}
                onSelected={async (userId) => {
                    const response = await ApiService.StudyDetails.DeleteReadUser(selectedRows.map(x => x.id), userId)
                    if (response.success)
                    {
                        refreshDataGrid()
                        closeModal()
                    }
                }}
            />
        ))
    }

    const removeRead = async () => {
        const response = await ApiService.StudyDetails.RemoveRead(selectedRows.map(x => x.id))
        if (response.success)
        {
            refreshDataGrid()
            closeModal()
        }
    }

    const setRead = async () => {
        if(!selectedRows || selectedRows.length === 0)
            return

        const read = async (userId) => {
            const response = await ApiService.StudyDetails.AddRead({
                studyIdList: selectedRows.map(x => x.id),
                isRead: true,
                readUserId: userId
            })

            if (response.success)
            {
                refreshDataGrid()
                return true
            }

            return false
        }

        if(hasPermission(PERMISSIONS.STUDY_SET_READ_USER)) {
            openModal(t('Doctors'), (
                <StudyUserAssignModal
                    selectDoctor={true}
                    autoSelect={true}
                    onSelected={async ({doctor, reporter}) => {
                        if(await read(doctor))
                            closeModal()
                    }}
                />
            ))
        }
        else {
            if(await read(user?.data?.id))
                closeModal()
        }
    }

    const downloadVoiceRecords = async () => {
        if(!selectedRows || selectedRows.length === 0)
            return

        const response = await ApiService.VoiceRecord.CreateZip(selectedRows.map(r => r.id));
        if(response.success)
        {
            ApiService.downloadFile(response.data, response.fileName);
        }
    }

    const openViewer = async () => {
        if(!selectedRows || selectedRows.length === 0)
            return

        const idList = await claimStudiesForViewing(selectedRows.map(r => r.id))
        if(idList.length === 0)
            return

        const response = await ApiService.Weasis.CreateWeasisUri(idList)
        if(response.success)
        {
            window.open(response.data, '_blank');
        }
    }


    const exportStudy = async () => {
        openModal(t('StudyExport'), (
            <StudyExportModal
                studyIdList={selectedRows.map(x => x.id)}
                onExport={async (exportName, studyIdList) => {
                    const res = await ApiService.Dicom.ExportStudies(exportName, studyIdList)
                    if(res.success) {
                        closeModal()
                    }
                }}
            />
        ))
    }

    const mergeStudiesToPatient = () => {
        if (selectedRows.length === 0) {
            return;
        }

        openModal(
            t('Merge Studies to Patient'),
            <MergeStudiesModal
                studyIdList={selectedRows.map(x => x.id)}
                studyInfo={selectedRows[0]}
                canCreatePatient={hasPermission(PERMISSIONS.PATIENT_CREATE_EDIT)}
                onCompleted={() => {
                    refreshDataGrid();
                    closeModal();
                }}
            />,
            null,       // kapanışta ek iş yok
            1000        // width — konumsal (bkz. shareStudies)
        );
    }

    const removeDoctorUser = async () => {
        const res = await ApiService.StudyUser.RemoveDoctorUser({
            studyIdList: selectedRows.map((r) => r.id),
            doctorUserId: 'remove',
            reporterUserId: null,
        });

        if (res.success) {
            refreshDataGrid();
            closeModal();
        }
    }

    const removeReporterUser = async () => {
        const res = await ApiService.StudyUser.RemoveReporterUser({
            studyIdList: selectedRows.map((r) => r.id),
            doctorUserId: null,
            reporterUserId: 'remove',
        });

        if (res.success) {
            refreshDataGrid();
            closeModal();
        }
    }

    const downloadPdf = async () => {
        const response = await ApiService.ReportGenerator.CreatePdfZip(selectedRows.map((r) => r.id));
        if (response.success) {
            ApiService.downloadFile(response.data, response.fileName);
        }
    }

    const downloadWord = async () => {
        const response = await ApiService.ReportGenerator.CreateWordZip(selectedRows.map((r) => r.id));
        if (response.success) {
            ApiService.downloadFile(response.data, response.fileName);
        }
    }

    const deleteStudies = async () => {
        const response = await ApiService.Study.DeleteStudies(selectedRows.map((r) => r.id));
        if (response.success) {
            await refreshDataGrid();
        }
    }

    const restoreStudies = async () => {
        const response = await ApiService.Study.RestoreStudies(selectedRows.map((r) => r.id));
        if (response.success) {
            await refreshDataGrid();
        }
    }

    const copyInfo = async () => {
        const texts = selectedRows?.map((row) =>
            [
                `ID: ${row.id}`,
                `${t('Patient ID')}: ${row.dicomPatientId}`,
                `${t('Patient Name')}: ${row.patientName}`,
                `${t('Accession Number')}: ${row.accessionNumber}`,
                `${t('Modality')}: ${row.modality}`,
                `${t('Description')}: ${row.studyDescription}`,
                `${t('Study Date')}: ${moment(row.studyDate).format('DD-MM-yyyy HH:mm')}`,
                `${t('Received Date')}: ${moment(row.receivedDate).format('DD-MM-yyyy HH:mm')}`,
            ].join('\n')
        );

        await copyToClipboard(texts.join('\n--------------------------\n'));
    }

    // Operasyonlar menüsü grupları: bir alt menü (ve çevresindeki ayırıcı) yalnız içinde
    // kullanıcının görebileceği en az bir öğe varsa çizilir.
    const canToggleRead =
        hasPermission(PERMISSIONS.STUDY_SET_READ) ||
        hasPermission(PERMISSIONS.STUDY_REMOVE_READ);
    const canRemoveAssignment =
        hasPermission(PERMISSIONS.STUDY_ASSIGN_DOCTOR_USER) ||
        hasPermission(PERMISSIONS.STUDY_ASSIGN_REPORTER_USER);
    const canDownload =
        hasPermission(PERMISSIONS.VOICE_RECORD_DOWNLOAD) ||
        hasPermission(PERMISSIONS.REPORT_DOWNLOAD_PDF) ||
        hasPermission(PERMISSIONS.REPORT_DOWNLOAD_WORD) ||
        hasPermission(PERMISSIONS.STUDY_OPEN_VIEWER);
    const canStudyOperations =
        hasPermission(PERMISSIONS.TELETIP_MANAGER) ||
        hasPermission(PERMISSIONS.STUDY_MERGE_PATIENT);
    const canDeleteStudies =
        hasPermission(PERMISSIONS.STUDY_DELETE_OR_RESTORE) ||
        hasPermission(PERMISSIONS.DELETE_STUDY_PERMANENT);

    return (
        <Flex
            flexDirection={'row'}
            alignItems={'center'}
            style={{ gap: 8 }}
            flexWrap={'wrap'}
        >
            <div>
                <Flex flexDirection={'row'}>
                    {colors.map((color) => {
                        return (
                            <div key={color.text}>
                                <Tooltip title={t(color.text)}>
                                    <div
                                        style={{
                                            width: 16,
                                            height: 16,
                                            backgroundColor: color.color,
                                            marginRight: 4,
                                        }}
                                    />
                                </Tooltip>
                            </div>
                        );
                    })}
                </Flex>
            </div>
            <Dropdown
                disabled={selectedRows.length === 0}
                trigger={'click'}
                overlayClassName={'grid-dropdown-overlay'}
                overlay={
                    <Menu>
                        {/*{(hasPermission(PERMISSIONS.STUDY_SET_EMERGENCY) ||*/}
                        {/*    hasPermission(*/}
                        {/*        PERMISSIONS.STUDY_REMOVE_EMERGENCY*/}
                        {/*    )) && (*/}
                        {/*    <>*/}
                        {/*        <Menu.Item key={'set-emergency-menu'}>*/}
                        {/*            <div*/}
                        {/*                style={{*/}
                        {/*                    display: 'flex',*/}
                        {/*                    alignItems: 'center',*/}
                        {/*                    gap: 4,*/}
                        {/*                }}*/}
                        {/*            >*/}
                        {/*                <div style={{ width: 100 }}>*/}
                        {/*                    {t('Emergency')}*/}
                        {/*                </div>*/}
                        {/*                <>*/}
                        {/*                    <Button*/}
                        {/*                        size={'small'}*/}
                        {/*                        style={{ width: 80 }}*/}
                        {/*                        onClick={async (e) => {*/}
                        {/*                            const response =*/}
                        {/*                                await ApiService.StudyDetails.SetStudyType(*/}
                        {/*                                    {*/}
                        {/*                                        studyIdList:*/}
                        {/*                                            selectedRows.map(*/}
                        {/*                                                (r) => {*/}
                        {/*                                                    return r.id;*/}
                        {/*                                                }*/}
                        {/*                                            ),*/}
                        {/*                                        studyType:*/}
                        {/*                                            StudyTypeEnum.EMERGENCY,*/}
                        {/*                                    }*/}
                        {/*                                );*/}

                        {/*                            if (response.success) {*/}
                        {/*                                refreshDataGrid();*/}
                        {/*                            }*/}
                        {/*                        }}*/}
                        {/*                    >*/}
                        {/*                        {t('Emergency')}*/}
                        {/*                    </Button>*/}
                        {/*                    <Button*/}
                        {/*                        size={'small'}*/}
                        {/*                        style={{ width: 80 }}*/}
                        {/*                        onClick={async (e) => {*/}
                        {/*                            const response =*/}
                        {/*                                await ApiService.StudyDetails.SetStudyType(*/}
                        {/*                                    {*/}
                        {/*                                        studyIdList:*/}
                        {/*                                            selectedRows.map(*/}
                        {/*                                                (r) => {*/}
                        {/*                                                    return r.id;*/}
                        {/*                                                }*/}
                        {/*                                            ),*/}
                        {/*                                        studyType:*/}
                        {/*                                            StudyTypeEnum.INPATIENT,*/}
                        {/*                                    }*/}
                        {/*                                );*/}

                        {/*                            if (response.success) {*/}
                        {/*                                refreshDataGrid();*/}
                        {/*                            }*/}
                        {/*                        }}*/}
                        {/*                    >*/}
                        {/*                        {t('Inpatient')}*/}
                        {/*                    </Button>*/}
                        {/*                    <Button*/}
                        {/*                        size={'small'}*/}
                        {/*                        style={{ width: 80 }}*/}
                        {/*                        onClick={async (e) => {*/}
                        {/*                            const response =*/}
                        {/*                                await ApiService.StudyDetails.SetStudyType(*/}
                        {/*                                    {*/}
                        {/*                                        studyIdList:*/}
                        {/*                                            selectedRows.map(*/}
                        {/*                                                (r) => {*/}
                        {/*                                                    return r.id;*/}
                        {/*                                                }*/}
                        {/*                                            ),*/}
                        {/*                                        studyType:*/}
                        {/*                                            StudyTypeEnum.ROUTINE,*/}
                        {/*                                    }*/}
                        {/*                                );*/}

                        {/*                            if (response.success) {*/}
                        {/*                                refreshDataGrid();*/}
                        {/*                            }*/}
                        {/*                        }}*/}
                        {/*                    >*/}
                        {/*                        {t('Routine')}*/}
                        {/*                    </Button>*/}
                        {/*                </>*/}
                        {/*            </div>*/}
                        {/*        </Menu.Item>*/}
                        {/*    </>*/}
                        {/*)}*/}

                        {(hasPermission(PERMISSIONS.STUDY_SET_READ) ||
                            hasPermission(PERMISSIONS.STUDY_REMOVE_READ)) && (
                            <Menu.Item key={'set-read-menu'}>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 4,
                                    }}
                                >
                                    <div style={{ width: 100 }}>
                                        {t('Read')}
                                    </div>
                                    {hasPermission(
                                        PERMISSIONS.STUDY_SET_READ
                                    ) && (
                                        <Button
                                            size={'small'}
                                            style={{ width: 80 }}
                                            onClick={async (e) => {
                                                await setRead();
                                            }}
                                        >
                                            {t('Read')} (T)
                                        </Button>
                                    )}
                                    {/*{hasPermission(PERMISSIONS.STUDY_REMOVE_READ) && (*/}
                                    {/*    <Button*/}
                                    {/*        size={'small'}*/}
                                    {/*        style={{width: 80}}*/}
                                    {/*        onClick={async (e) => {*/}
                                    {/*            await removeRead()*/}
                                    {/*        }}*/}
                                    {/*    >*/}
                                    {/*        {t('Not Read')}*/}
                                    {/*    </Button>*/}
                                    {/*)}*/}
                                    {hasPermission(
                                        PERMISSIONS.STUDY_REMOVE_READ
                                    ) && (
                                        <Button
                                            size={'small'}
                                            style={{ width: 80 }}
                                            onClick={async (e) => {
                                                await deleteReadUser();
                                            }}
                                        >
                                            {t('Not Read')}
                                        </Button>
                                    )}
                                </div>
                            </Menu.Item>
                        )}

                        {canRemoveAssignment && (
                            <Menu.Item key={'remove-assignment'}>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 4,
                                    }}
                                >
                                    <div style={{ width: 100 }}>
                                        {t('Remove Assignment')}
                                    </div>
                                    {hasPermission(PERMISSIONS.STUDY_ASSIGN_DOCTOR_USER) && (
                                        <Button
                                            size={'small'}
                                            style={{ width: 80 }}
                                            onClick={removeDoctorUser}
                                        >
                                            {t('Doctor')}
                                        </Button>
                                    )}
                                    {hasPermission(PERMISSIONS.STUDY_ASSIGN_REPORTER_USER) && (
                                        <Button
                                            size={'small'}
                                            style={{ width: 80 }}
                                            onClick={removeReporterUser}
                                        >
                                            {t('Reporter Short')}
                                        </Button>
                                    )}
                                </div>
                            </Menu.Item>
                        )}

                        {canRemoveAssignment && (
                            <Menu.Item
                                key={'assign-user'}
                                onClick={() => {
                                    openModal(
                                        t('Assign User'),
                                        <StudyUserAssignModal
                                            selectDoctor={hasPermission(
                                                PERMISSIONS.STUDY_ASSIGN_DOCTOR_USER
                                            )}
                                            selectReporter={hasPermission(
                                                PERMISSIONS.STUDY_ASSIGN_REPORTER_USER
                                            )}
                                            autoSelect={false}
                                            onSelected={async ({
                                                doctor,
                                                reporter,
                                            }) => {
                                                let resDoctor = null
                                                let resReporter = null

                                                if (
                                                    hasPermission(
                                                        PERMISSIONS.STUDY_ASSIGN_DOCTOR_USER
                                                    )
                                                ) {
                                                    resDoctor =
                                                        await ApiService.StudyUser.AssignDoctorUser(
                                                            {
                                                                studyIdList:
                                                                    selectedRows.map(
                                                                        (r) =>
                                                                            r.id
                                                                    ),
                                                                doctorUserId:
                                                                    doctor,
                                                            }
                                                        );
                                                }
                                                    
                                                if (
                                                    hasPermission(
                                                        PERMISSIONS.STUDY_ASSIGN_REPORTER_USER
                                                    )
                                                ) {
                                                    resReporter =
                                                        await ApiService.StudyUser.AssignReporterUser(
                                                            {
                                                                studyIdList:
                                                                    selectedRows.map(
                                                                        (r) =>
                                                                            r.id
                                                                    ),
                                                                reporterUserId:
                                                                    reporter,
                                                            }
                                                        );
                                                }
                                                    

                                                if (
                                                    resDoctor.success ||
                                                    resReporter.success
                                                ) {
                                                    refreshDataGrid();
                                                    closeModal();
                                                }
                                            }}
                                        />
                                    );
                                }}
                            >
                                <div>{t('Assign User')}</div>
                            </Menu.Item>
                        )}
                        {(canToggleRead || canRemoveAssignment) && (
                            <Menu.Divider key={'operations-divider'} />
                        )}

                        {canDownload && (
                            <Menu.SubMenu key={'download'} title={t('Download')}>
                                {hasPermission(PERMISSIONS.VOICE_RECORD_DOWNLOAD) && (
                                    <Menu.Item key={'download-voice-records'} onClick={downloadVoiceRecords}>
                                        {t('Voice Records') + ' (V)'}
                                    </Menu.Item>
                                )}
                                {hasPermission(PERMISSIONS.REPORT_DOWNLOAD_PDF) && (
                                    <Menu.Item key={'download-pdf'} onClick={downloadPdf}>
                                        PDF
                                    </Menu.Item>
                                )}
                                {hasPermission(PERMISSIONS.REPORT_DOWNLOAD_WORD) && (
                                    <Menu.Item key={'download-word'} onClick={downloadWord}>
                                        Word
                                    </Menu.Item>
                                )}
                                {hasPermission(PERMISSIONS.STUDY_OPEN_VIEWER) && (
                                    <Menu.Item key={'download-dicom-zip'} onClick={downloadStudies}>
                                        Dicom Zip
                                    </Menu.Item>
                                )}
                            </Menu.SubMenu>
                        )}

                        {canStudyOperations && (
                            <Menu.SubMenu key={'study-operations'} title={t('Study')}>
                                {hasPermission(PERMISSIONS.TELETIP_MANAGER) && (
                                    <Menu.Item key={'MakeKOS'} onClick={makeKos}>
                                        {t('Make KOS')}
                                    </Menu.Item>
                                )}
                                {hasPermission(PERMISSIONS.STUDY_MERGE_PATIENT) && (
                                    <Menu.Item key={'merge-patient'} onClick={mergeStudiesToPatient}>
                                        {t('Merge Patient')}
                                    </Menu.Item>
                                )}
                            </Menu.SubMenu>
                        )}

                        <Menu.Item key={'copy-info'} onClick={copyInfo}>
                            <div>{t('Copy Info')}</div>
                        </Menu.Item>

                        {canDeleteStudies && <Menu.Divider key={'delete-divider'} />}

                        {/* Silme işlemleri alt menüde: yanlışlıkla tıklanmasınlar diye. */}
                        {canDeleteStudies && (
                            <Menu.SubMenu key={'delete'} title={t('Delete')}>
                                {hasPermission(PERMISSIONS.STUDY_DELETE_OR_RESTORE) && (
                                    <Menu.Item key={'delete-studies'} onClick={deleteStudies}>
                                        {t('Delete Studies')}
                                    </Menu.Item>
                                )}
                                {hasPermission(PERMISSIONS.STUDY_DELETE_OR_RESTORE) && (
                                    <Menu.Item key={'restore-studies'} onClick={restoreStudies}>
                                        {t('Restore Studies')}
                                    </Menu.Item>
                                )}
                                {hasPermission(PERMISSIONS.STUDY_DELETE_OR_RESTORE) &&
                                    hasPermission(PERMISSIONS.DELETE_STUDY_PERMANENT) && (
                                        <Menu.Divider key={'delete-permanent-divider'} />
                                    )}
                                {hasPermission(PERMISSIONS.DELETE_STUDY_PERMANENT) && (
                                    <Menu.Item
                                        key={'delete-studies-permanent'}
                                        onClick={() => {
                                            confirmPermanentDelete({
                                                t,
                                                studyIdList: selectedRows.map((r) => r.id),
                                                onDeleted: refreshDataGrid,
                                            });
                                        }}
                                    >
                                        <span style={{ color: '#cf1322' }}>
                                            {t('Delete Studies Permanent')}
                                        </span>
                                    </Menu.Item>
                                )}
                            </Menu.SubMenu>
                        )}
                    </Menu>
                }
            >
                <Button size={'small'} type={'primary'}>
                    {t('Operations')}
                    <DownOutlined />
                </Button>
            </Dropdown>

            {hasPermission(PERMISSIONS.STUDY_OPEN_VIEWER) && (
                <Button
                    size={'small'}
                    disabled={selectedRows.length === 0}
                    onClick={openViewer}
                >
                    {t('Viewer') + ' (D)'}
                </Button>
            )}

            {hasPermission(PERMISSIONS.STUDY_OPEN_VIEWER) && user?.config?.enableWebViewer &&(
                <Button
                    size={'small'}
                    disabled={selectedRows.length === 0}
                    onClick={openWebViewer}
                >
                    {t('Web Viewer') + ' (W)'}
                </Button>
            )}

            {hasPermission(PERMISSIONS.EXPORT_STUDY) && (
                <Button
                    size={'small'}
                    disabled={selectedRows.length === 0}
                    onClick={exportStudy}
                >
                    {t('Export Study')}
                </Button>
            )}

            {hasPermission(PERMISSIONS.STUDY_SHARE) && (
                <Button
                    size={'small'}
                    disabled={selectedRows.length === 0}
                    onClick={shareStudies}
                >
                    {t('Share')}
                </Button>
            )}

            {hasPermission(PERMISSIONS.STUDY_CREATE_EMPTY) && (
                <Button
                    className={'btn-accent'}
                    size={'small'}
                    onClick={() => {
                        openModal(
                            t(''),
                            <StudyCreateModal
                                onSuccess={() => {
                                    closeModal();
                                }}
                            />
                        );
                    }}
                >
                    {t('Create Study')}
                </Button>
            )}

            {/*<Button*/}
            {/*    type={'primary'}*/}
            {/*    onClick={() => {*/}
            {/*        hubConnection.invoke("SendMessage", 'user-test', 'Hello SignalR').then((res)=>{})*/}
            {/*    }}*/}
            {/*>*/}
            {/*    SignalR*/}
            {/*</Button>*/}
        </Flex>
    );
}
